import {Plus} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Label} from '@common-web/components/ui/label';
import {
  TextField,
  TextArea,
  SegmentsEditor,
  StringListEditor,
  IconColorPicker,
  Field,
  UseDefaultToggle,
  EditorCard,
  moveItem,
  sameConfig,
  type IconOption,
  type SectionProps,
} from '../controls';
import {COVERAGE_ICON_POOL, COVERAGE_COLOR_POOL} from '@client/pages/public/enroll/coverageIcons';
import type {CareCard, CoverageCard, CoverageIcon} from '@common/enrollmentSite/types';
import {DEFAULT_CARE_CARDS} from '@common/enrollmentSite/defaults/careCards';
import {MirrorLock} from '../mirrorLock';

const COVERAGE_ICON_OPTIONS: IconOption[] = Object.entries(COVERAGE_ICON_POOL).map(
  ([value, {Icon, label}]) => ({value, label, Icon}),
);

function IconTitleRow({
  icon,
  color,
  title,
  onChange,
}: {
  icon: CoverageIcon;
  color: string;
  title: string;
  onChange: (next: {icon?: CoverageIcon; color?: string; title?: string}) => void;
}) {
  return (
    <div className='flex items-end gap-3'>
      <div className='space-y-1.5'>
        <Label className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Icon
        </Label>
        <IconColorPicker
          icon={icon}
          color={color}
          onIconChange={(v) => onChange({icon: v as CoverageIcon})}
          onColorChange={(color) => onChange({color})}
          icons={COVERAGE_ICON_OPTIONS}
          colors={COVERAGE_COLOR_POOL}
        />
      </div>
      <div className='flex-1'>
        <TextField label='Title' value={title} onChange={(title) => onChange({title})} />
      </div>
    </div>
  );
}

export function LandingSection({config, patch}: SectionProps) {
  const cards = config.coverageCards ?? [];
  const careCards = config.careCards;
  const careDefault = careCards === undefined;

  function updateCard(i: number, next: Partial<CoverageCard>) {
    patch({coverageCards: cards.map((c, idx) => (idx === i ? {...c, ...next} : c))});
  }
  function addCard() {
    // Rotate defaults so consecutive new cards don't all look identical.
    const icon = COVERAGE_ICON_OPTIONS[cards.length % COVERAGE_ICON_OPTIONS.length]
      .value as CoverageIcon;
    const color = COVERAGE_COLOR_POOL[cards.length % COVERAGE_COLOR_POOL.length].value;
    patch({coverageCards: [...cards, {icon, color, title: '', bullets: []}]});
  }

  function setCareCards(next: CareCard[]) {
    patch({careCards: next});
  }
  function updateCareCard(i: number, next: Partial<CareCard>) {
    setCareCards((careCards ?? []).map((c, idx) => (idx === i ? {...c, ...next} : c)));
  }
  function toggleCareDefault(usingDefault: boolean) {
    if (usingDefault) patch({careCards: undefined});
    else setCareCards(DEFAULT_CARE_CARDS.map((c) => ({...c})));
  }

  return (
    <div className='space-y-5'>
      <MirrorLock field='heroHeadline'>
        <SegmentsEditor
          label='Hero headline'
          hint='The landing H1, split into runs. Mark any run as accent-colored.'
          segments={config.heroHeadline ?? []}
          onChange={(heroHeadline) => patch({heroHeadline})}
        />
      </MirrorLock>
      <MirrorLock field='heroTagline'>
        <TextField
          label='Hero tagline'
          hint='Italic subhead under the headline.'
          value={config.heroTagline}
          onChange={(heroTagline) => patch({heroTagline})}
        />
      </MirrorLock>
      <MirrorLock field='heroBlurb'>
        <TextArea
          label='Lead text'
          hint='Intro paragraph under the tagline.'
          value={config.heroBlurb}
          onChange={(heroBlurb) => patch({heroBlurb})}
        />
      </MirrorLock>
      <MirrorLock field='landingVideo'>
        <TextField
          label='Hero video URL'
          hint='Optional autoplay video on the landing page. For reliable playback, hand the raw file to engineering to host on Mux and paste the Mux URL here — avoid linking an unmanaged source.'
          value={config.landingVideoUrl ?? ''}
          onChange={(v) => patch({landingVideoUrl: v || undefined})}
        />
      </MirrorLock>

      <MirrorLock field='careCards'>
        <UseDefaultToggle
          label='Care cards'
          hint="The 'Real care, when you need it' row. Uncheck to write this site's own cards; {phone} in a body becomes the support phone."
          usingDefault={careDefault}
          isCustomized={!careDefault && !sameConfig(careCards, DEFAULT_CARE_CARDS)}
          onChange={toggleCareDefault}
        />
        {careDefault ? (
          <ul className='space-y-1 rounded-lg border bg-muted/20 p-3 text-sm text-muted-foreground'>
            {DEFAULT_CARE_CARDS.map((c) => (
              <li key={c.title}>{c.title}</li>
            ))}
          </ul>
        ) : (
          <div className='space-y-3'>
            {(careCards ?? []).map((card, i) => (
              <EditorCard
                key={i}
                index={i}
                count={careCards?.length ?? 0}
                title={card.title || 'Untitled card'}
                onMove={(dir) => setCareCards(moveItem(careCards ?? [], i, dir))}
                onRemove={() => setCareCards((careCards ?? []).filter((_, idx) => idx !== i))}
              >
                <IconTitleRow
                  icon={card.icon}
                  color={card.color}
                  title={card.title}
                  onChange={(n) => updateCareCard(i, n)}
                />
                <TextArea
                  label='Body'
                  rows={2}
                  value={card.body}
                  onChange={(body) => updateCareCard(i, {body})}
                />
              </EditorCard>
            ))}
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                const n = careCards?.length ?? 0;
                setCareCards([
                  ...(careCards ?? []),
                  {
                    icon: COVERAGE_ICON_OPTIONS[n % COVERAGE_ICON_OPTIONS.length]
                      .value as CoverageIcon,
                    color: COVERAGE_COLOR_POOL[n % COVERAGE_COLOR_POOL.length].value,
                    title: '',
                    body: '',
                  },
                ]);
              }}
            >
              <Plus className='mr-1.5 h-3.5 w-3.5' />
              Add care card
            </Button>
          </div>
        )}
      </MirrorLock>

      <MirrorLock field='coverageCards'>
        <Field
          label='Coverage cards'
          hint='Highlight cards in the "rooted in flexibility" row.'
          hintTop
        >
          <div className='space-y-3'>
            {cards.map((card, i) => (
              <EditorCard
                key={i}
                index={i}
                count={cards.length}
                title={card.title || 'Untitled card'}
                onMove={(dir) => patch({coverageCards: moveItem(cards, i, dir)})}
                onRemove={() => patch({coverageCards: cards.filter((_, idx) => idx !== i)})}
              >
                <IconTitleRow
                  icon={card.icon}
                  color={card.color}
                  title={card.title}
                  onChange={(n) => updateCard(i, n)}
                />
                <StringListEditor
                  label='Bullets'
                  items={card.bullets ?? []}
                  onChange={(bullets) => updateCard(i, {bullets})}
                />
              </EditorCard>
            ))}
            <Button variant='outline' size='sm' onClick={addCard}>
              <Plus className='mr-1.5 h-3.5 w-3.5' />
              Add coverage card
            </Button>
          </div>
        </Field>
      </MirrorLock>
    </div>
  );
}
